import moment from 'moment'
import i18n from '@/typescript/servicos/i18n/i18n.servico'
import { modalDeErro } from 'src/utils'
import { obterDiaInicioFolha } from '@/integracoes/src-next/catalogos/periodo-folha-empregado'
import { multiSelect } from 'src/common/selectors'
import { copiarObjetoProfundamente, obterDataValida } from 'src/utils/formatadoras/formatadoras'
import { resetarParaEstadoInicial } from 'src/utils/executoras/executoras'
import router from 'src/router'
import { API } from 'src/services'
import store from 'src/store'
import { empresaUsaApisRefatoracaoFolha } from '../utils'

const NOW = moment()

const defaultState = () => ({
  ...multiSelect.mapStates(['companies', 'employees', 'userGroups']),
  startDate: NOW.toDate(),
  endDate: NOW.toDate(),
  dataDoPeriodo: NOW.toDate(),
  closedPeriod: null,
  type: null,
  typeLoadCompany: store?.state.userInfo.compMan.frontOptions.typeLoadCompany || 'perday',
  queueEmployees: [], // list of employee, e.g {name, id, closingDate}
  queueCurrentIndex: null, // index of what item we are showing of the queueEmployees
  typeEmployeeOrder: 'name'
})

const state = defaultState()

/**
 * Data de referência para trocar de empregado na fila quando não há período fechado.
 * Com as APIs V2, startDate/endDate podem ficar null (folha vazia); dataDoPeriodo mantém a competência.
 */
function obterMomentReferenciaParaFilaSemPeriodoFechado (state) {
  let date = moment(state.dataDoPeriodo)
  if (date.isValid()) return date
  date = moment(state.startDate)
  if (date.isValid()) return date
  date = moment(state.endDate)
  if (date.isValid()) return date
  return moment()
}

const getters = {
  startDate (state) {
    return moment(state.startDate)
  },
  endDate (state) {
    return moment(state.endDate)
  },
  dataDoPeriodo (state) {
    return moment(state.dataDoPeriodo)
  },
  currentQueueEmployee (state) {
    return state.queueEmployees.length ? state.queueEmployees[state.queueCurrentIndex] : null
  }
}

function atualizarUrlDaFolha (state, id, porPeriodo = false) {
  const fim = getters.endDate(state)
  const caminhoPorIntervalo = `/timesheet/${state.type}/${getters.startDate(state).format('YYYY-MM-DD')}/${fim.isValid() ? fim.format('YYYY-MM-DD') : 'null'}/${id}`
  const caminhoPorPeriodo = `/timesheet/${state.type}/${getters.dataDoPeriodo(state).format('YYYY-MM-DD')}/${id}`
  const atual = router.currentRoute.value
  // A carga não consome a intenção do JEV. Só o formulário/popover pode fazê-lo.
  // Aceita as duas representações da mesma folha, sem transportar a intenção
  // para outro empregado ou período e sem restaurar queries já consumidas.
  const mesmaFolha = atual.path === caminhoPorIntervalo || atual.path === caminhoPorPeriodo
  return router.replace({
    path: porPeriodo ? caminhoPorPeriodo : caminhoPorIntervalo,
    query: mesmaFolha ? atual.query : {}
  })
}

const mutations = {
  resetState: state => {
    const toBe = defaultState()
    delete toBe['companies']
    delete toBe['employees']
    delete toBe['userGroups']
    resetarParaEstadoInicial(state, toBe)
  },
  ...multiSelect.mapMutations(['companies', 'employees', 'userGroups']),
  typeLoadCompany: (state, payload) => { state.typeLoadCompany = payload },
  type: (state, payload) => {
    state.type = payload
  },
  startDate: (state, payload) => { state.startDate = obterDataValida(payload) },
  endDate: (state, payload) => { state.endDate = obterDataValida(payload) },
  dataDoPeriodo: (state, payload) => {
    const candidato = moment.isMoment(payload) ? payload.clone() : moment(payload)
    if (payload == null || !candidato.isValid()) {
      const anterior = moment(state.dataDoPeriodo)
      state.dataDoPeriodo = anterior.isValid() ? anterior.toDate() : new Date()
      return
    }
    state.dataDoPeriodo = obterDataValida(payload)
  },
  datePeriod: (state, payload) => {
    state.startDate = obterDataValida(payload.startDate)
    state.endDate = obterDataValida(payload.endDate)
  },
  setOptions (state, payload) {
    state.type = payload.type
    state.startDate = obterDataValida(payload.startDate)
    state.endDate = obterDataValida(payload.endDate)
  },
  queueEmployees (state, payload) {
    state.queueCurrentIndex = 0
    state.queueEmployees = payload || []
  },
  typeEmployeeOrder (state, payload) {
    state.typeEmployeeOrder = payload
  },
  queueCurrentIndex (state, payload) {
    state.queueCurrentIndex = payload
  },
  _updateUrlAndHeaderSelectors (state, { company = null, userGroup = null }) {
    // Only states should call this function, actually, today only the populateLines call it
    let id = ''
    if (state.type === 'work-place' && state.companies.selected.length >= 1 && state.typeLoadCompany !== 'peremp') {
      id = company || state.companies.selected[0].id
    } else if (state.type === 'employee') {
      id = state.queueEmployees[state.queueCurrentIndex].id
    } else if (state.type === 'user-group') {
      id = userGroup || state.userGroups.selected[0].id
    }

    atualizarUrlDaFolha(state, id)
  },
  _atualizarUrlESeletoresHeader (state, { company = null, userGroup = null }) {
    // Only states should call this function, actually, today only the populateLines call it
    let id = ''
    if (state.type === 'work-place' && state.companies.selected.length >= 1 && state.typeLoadCompany !== 'peremp') {
      id = company || state.companies.selected[0].id
    } else if (state.type === 'employee') {
      id = state.queueEmployees[state.queueCurrentIndex].id
    } else if (state.type === 'user-group') {
      id = userGroup || state.userGroups.selected[0].id
    }

    atualizarUrlDaFolha(state, id, true)
  },
  closedPeriod (state, closedPeriod = null) {
    if (closedPeriod) {
      state.startDate = obterDataValida(moment(closedPeriod.startPeriod))
      state.endDate = obterDataValida(moment(closedPeriod.endPeriod))
      state.closedPeriod = copiarObjetoProfundamente(closedPeriod)
    } else {
      state.closedPeriod = null
    }
  }
}

const actions = {
  startDate: ({ commit }, payload) => { commit('startDate', payload) },
  endDate: ({ commit }, payload) => { commit('endDate', payload) },
  nextEmployee ({ state, commit, dispatch }, payload) {
    if (state.type === 'work-place') {
      commit('type', 'employee')
    }
    const index = payload && payload.fromStart ? 0 : state.queueCurrentIndex + 1
    commit('queueCurrentIndex', index)

    let date
    if (state.closedPeriod) {
      date = moment(new Date(state.closedPeriod.year, state.closedPeriod.month - 1, 1))
    } else if (empresaUsaApisRefatoracaoFolha()) {
      date = obterMomentReferenciaParaFilaSemPeriodoFechado(state)
    } else {
      date = moment(state.endDate)
    }

    if (empresaUsaApisRefatoracaoFolha()) {
      dispatch('construirFolha', { dataBase: date })
    } else {
      dispatch('calcPeriod', { baseDate: date })
    }
  },
  prevEmployee ({ state, commit, dispatch }) {
    const index = state.queueCurrentIndex - 1
    commit('queueCurrentIndex', index)

    let date
    if (state.closedPeriod) {
      date = moment(new Date(state.closedPeriod.year, state.closedPeriod.month - 1, 1))
    } else if (empresaUsaApisRefatoracaoFolha()) {
      date = obterMomentReferenciaParaFilaSemPeriodoFechado(state)
    } else {
      date = moment(state.endDate)
    }

    if (empresaUsaApisRefatoracaoFolha()) {
      dispatch('construirFolha', { dataBase: date })
    } else {
      dispatch('calcPeriod', { baseDate: date })
    }
  },
  /**
   * @deprecated Não é mais usado na nova estrutura
   */
  async calcPeriod ({ state, commit, dispatch }, { type = null, baseDate = null, closingSheet = false }) {
    commit('sheet/loading', {lines: true}, {root: true})

    // closingSheet: set to true if is to calc employee timesheet date in current closing sheet
    if (state.type === 'employee') {
      const employee = state.queueEmployees[state.queueCurrentIndex]
      const diaInicioFolha = obterDiaInicioFolha(employee?.closingDate)
      if (diaInicioFolha === null) {
        commit('sheet/loading', { lines: false }, { root: true })
        modalDeErro(i18n.global.t('srcNext.estruturaDePontoDaEmpresa.catalogoEmpregado.erroPeriodoEmpregado'))
        return
      }

      let usuarioAcessandoSuaFolhaAtual = false

      if (state.closedPeriod && !baseDate) {
        const { year, month } = state.closedPeriod
        baseDate = moment(new Date(year, month - 1, 1))
      } else {
        if (!baseDate) {
          usuarioAcessandoSuaFolhaAtual = true
        }
        baseDate = moment(baseDate || state.endDate)
      }

      // Gambiarra para arrumar o erro de pegar a folha seguinte quando usuário de funcionário entra na própria folha (user-story #3081)
      const dataCorteDoMesDiferenteDe1 = diaInicioFolha !== 1
      const dataDeHojeEhMenorQueDataDeCorteDoMes = NOW.date() < diaInicioFolha && NOW.month() < baseDate.month()
      if (usuarioAcessandoSuaFolhaAtual && dataCorteDoMesDiferenteDe1 && dataDeHojeEhMenorQueDataDeCorteDoMes) {
        type = 'sub'
      }

      if (type) {
        baseDate = type === 'add' ? baseDate.add(1, 'month') : baseDate.subtract(1, 'month')
      }

      if (closingSheet) {
        baseDate = moment()
      }

      // Carga FRIA da rota: `state.endDate` ainda não foi preenchido, e
      // `moment('')` é inválido — `month()`/`year()` viram NaN e a chamada sai
      // como `/api/closed-period/<id>/NaN/NaN`, que o dono recusa com 400 e o
      // overlay de erro cobre a grade inteira. Medido em 20/09/2026 abrindo a
      // folha por link direto: 60 chamadas assim numa carga só.
      //
      // Sem data não há período a consultar: o certo é não perguntar, e não
      // perguntar errado. A folha segue sem `closedPeriod`, que é o mesmo
      // estado do ramo de baixo.
      if (!baseDate.isValid()) {
        commit('closedPeriod', null)
        return
      }

      const result = await API.closedPeriod.get({ id: employee.id, month: baseDate.month() + 1, year: baseDate.year() })
      commit('closedPeriod', result.data.success)
    } else {
      baseDate = baseDate || moment(state.endDate)

      if (type === 'add') {
        baseDate.add(1, 'day')
      } else if (type === 'subtract') {
        baseDate.subtract(1, 'day')
      }
      const period = { startDate: baseDate.toDate(), endDate: baseDate.toDate() }
      commit('datePeriod', period)
      commit('closedPeriod', null)
    }

    dispatch('timesheet/loadSheet', null, { root: true })
  },
  async construirFolha({ state, commit, dispatch }, { dataBase }) {
    commit('sheet/cancelarTodasAsRequisicoesPendentes', undefined, { root: true })
    commit('timesheet/sumTimes', [], { root: true })
    commit('timesheet/sumWhb', { monthly: null, overal: null }, { root: true })
    commit('timesheet/loadingSumTimes', true, { root: true })
    commit('sheet/loading', { lines: true, pts: true, djs: true, tms: true, justifies: true, whbs: true }, { root: true })

    if (!dataBase) {
      dataBase = moment(state.dataDoPeriodo).toDate()
    }

    await commit('dataDoPeriodo', dataBase)

    const argumentos = await dispatch('timesheet/preCarregarFolha', null, { root: true })

    commit('timesheet/dataUsedToPopulate', argumentos, { root: true })

    await dispatch('sheet/carregarFolha', argumentos, { root: true })

    await dispatch('sheet/popularLinhasDaFolha', { dataInicio: state.startDate, dataFim: state.endDate }, { root: true })

    await dispatch('timesheet/posPopularLinhasDaFolha', null, { root: true })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
